import { ProductionTitleText } from "../../../-components/production-title"
import { StatusBadge } from "../../../-components/status-badge"
import { getFormattedDate } from "../../../../../utils/get-formatted-date"
import { ActivityTypeText } from "./activity-type-text"
import { useTranslation } from "react-i18next"
import { useTimePeriod } from "../../../../../utils/use-time-period"
import { useSuspenseQueryGet } from "../../../../../typed-client/hooks/use-suspense-query-get"
import { FieldData } from "../../../../../-components/field-data"
import { TheasoftColumnDef } from "@/routes/-components/custom-tanstack-table"
import {
  ActivityDto,
  ActivityTypeDto,
  ProductionTitle,
  StatusDto,
  TimePeriod,
  type FieldData as TFieldData,
} from "@/orval/gen"
import { DateOnly } from "@/@client/System"
import { VisibilityState } from "@tanstack/react-table"

type ActivityTableColumns = {
  baseColumns: TheasoftColumnDef<ActivityDto>[]
  extendedColumns: TheasoftColumnDef<ActivityDto>[]
  defaultColumnVisibility: VisibilityState
}

// TODO: components
export function useActivityTableColumns(): ActivityTableColumns {
  const { t } = useTranslation("app_server")

  const { getTimePeriod } = useTimePeriod()

  const { data: activityFieldTypes } = useSuspenseQueryGet(
    "/api/ProductionFieldType/GetActivityFieldTypes",
    null
  )

  const baseColumns = React.useMemo<TheasoftColumnDef<ActivityDto>[]>(
    () => [
      {
        id: "date",
        accessorKey: "date",
        header: t("Date"),
        cell: ({ cell }) =>
          getFormattedDate(cell.getValue<DateOnly>(), "ddd L"),
      },
      {
        id: "timePeriod",
        accessorKey: "timePeriod",
        header: t("TimePeriod"),
        cell: ({ cell }) => getTimePeriod(cell.getValue<TimePeriod>()),
      },
      {
        id: "activityType",
        accessorKey: "activityType",
        header: t("ActivityType"),
        cell: ({ cell, row }) => {
          const activityType = cell.getValue<ActivityTypeDto>()
          return (
            <ActivityTypeText
              name={activityType.name}
              rehearseNumber={row.original.rehearseNumber}
              foreColor={activityType.foreColor}
              backColor={activityType.backColor}
              truncate
              fz="sm"
            />
          )
        },
        enableColumnFilter: false,
      },
      {
        id: "title",
        accessorKey: "title",
        header: t("Title"),
        cell: ({ cell }) => (
          <ProductionTitleText
            productionTitle={cell.getValue<ProductionTitle>()}
            fz="sm"
          />
        ),
      },
      { id: "location", accessorKey: "location", header: t("Location") },
      {
        id: "artisticNote",
        accessorKey: "artisticNote",
        header: t("ArtisticNote"),
        enableSorting: false,
      },
      {
        id: "technicalNote",
        accessorKey: "technicalNote",
        header: t("TechnicalNote"),
        enableSorting: false,
      },
      {
        id: "status",
        accessorKey: "status",
        header: t("Status"),
        enableColumnFilter: false,
        cell: ({ cell }) => <StatusBadge status={cell.getValue<StatusDto>()} />,
      },
    ],
    []
  )

  const extendedColumns = React.useMemo<TheasoftColumnDef<ActivityDto>[]>(
    () => [
      { id: "activityId", accessorKey: "activityId", header: "Id" },
      ...baseColumns,
      ...activityFieldTypes.map<TheasoftColumnDef<ActivityDto>>((v) => ({
        id: `activityField-${v.id}`,
        accessorFn: (row) =>
          row.activityFields?.find((field) => field.type.id === v.id),
        header: v.name,
        cell: ({ cell }) => {
          const cellValue = cell.getValue<TFieldData | undefined>()
          return cellValue ? <FieldData fieldData={cellValue} /> : null
        },
        enableColumnFilter: false,
        enableSorting: false,
      })),
    ],
    []
  )

  const defaultColumnVisibility = React.useMemo<VisibilityState>(
    () => ({
      activityId: false,
      ...activityFieldTypes.reduce(
        (acc, v) => {
          acc[`activityField-${v.id}`] = false
          return acc
        },
        {} as Record<string, boolean>
      ),
    }),
    [activityFieldTypes]
  )

  return { baseColumns, extendedColumns, defaultColumnVisibility }
}

